import { Bell, Settings } from 'lucide-react'
import { Link } from 'react-router'
import { EngineConnectionCard } from '@/components/settings/engine-connection-card'
import { SessionSettingsCard } from '@/components/settings/session-settings-card'
import { SlackIntegrationCard } from '@/components/settings/slack-integration-card'
import { LoadingPage } from '@/components/ui/loading'
import { useSettings } from '@/hooks/use-settings'

export function SettingsPage(): React.JSX.Element {
  const settingsQuery = useSettings()

  if (settingsQuery.isLoading) {
    return <LoadingPage message="Loading settings..." />
  }

  if (settingsQuery.error || !settingsQuery.data) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <Settings className="mb-4 h-12 w-12 text-text-tertiary" />
        <p className="text-sm text-text-secondary">Failed to load settings. Please try again.</p>
      </div>
    )
  }

  const settings = settingsQuery.data

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Settings className="h-6 w-6 text-admin-500" />
        <h1 className="text-2xl font-semibold text-text-primary">Settings</h1>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <EngineConnectionCard engineUrl={settings.engine_url} isHealthy={settings.engine_healthy} />

        <AlertDefaultsCard />

        <SlackIntegrationCard settings={settings.slack} />

        <SessionSettingsCard
          sessionDurationHours={settings.session_duration_hours}
          otpExpiryMinutes={settings.otp_expiry_minutes}
        />
      </div>
    </div>
  )
}

function AlertDefaultsCard(): React.JSX.Element {
  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
      <div className="mb-4 flex items-center gap-2">
        <Bell className="h-5 w-5 text-admin-500" />
        <h2 className="text-sm font-medium text-text-primary">Alert Defaults</h2>
      </div>
      <p className="text-sm text-text-secondary">
        Alert rules and thresholds are configured on the Alerts page.
      </p>
      <Link
        to="/alerts"
        className="mt-3 inline-block text-sm font-medium text-admin-500 hover:text-admin-600"
      >
        Go to Alerts Configuration
      </Link>
    </div>
  )
}
